import {
  PREVIEW_CAPTURE_EVENT,
  PREVIEW_CAPTURE_TIMEOUT_MS,
  parsePreviewCaptureResult,
  type PreviewCaptureRequest,
  type PreviewCaptureResult,
} from './capture';

export interface PreviewCaptureEventDetail {
  request: PreviewCaptureRequest;
  signal: AbortSignal;
  respond: (result: PreviewCaptureResult) => void;
}

export function requestPreviewCapture(
  request: PreviewCaptureRequest,
  signal?: AbortSignal
): Promise<PreviewCaptureResult> {
  return new Promise(resolve => {
    const controller = new AbortController();
    let settled = false;
    const finish = (result: PreviewCaptureResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abort);
      controller.abort();
      try {
        resolve(parsePreviewCaptureResult(result, request));
      } catch {
        resolve({ status: 'unavailable', error: 'The preview returned an invalid capture.' });
      }
    };
    const abort = () => finish({ status: 'unavailable', error: 'The capture turn ended.' });
    const timeout = setTimeout(
      () => finish({ status: 'timeout', error: 'Preview capture timed out.' }),
      PREVIEW_CAPTURE_TIMEOUT_MS
    );
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) {
      abort();
      return;
    }
    const event = new CustomEvent<PreviewCaptureEventDetail>(PREVIEW_CAPTURE_EVENT, {
      cancelable: true,
      detail: { request, signal: controller.signal, respond: finish },
    });
    window.dispatchEvent(event);
    if (!event.defaultPrevented)
      finish({
        status: 'unavailable',
        error: 'The requested asset editor is not open in this task.',
      });
  });
}
